import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    Box,
    Typography,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Popper,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import JournalEntryTemplateService from '../../../Services/journalEntryTemplate.service';
import Constants from '../../../assests/Constants/constants';
import { JournalEntryTypes, JOURNAL_ENTRY_TYPE_OPTIONS, journalEntryTypeLabel } from '../../../assests/Constants/journalEntryTypes';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import fieldStyles from './AddJournalEntryModal.module.scss';
import modalStyles from './JournalEntryTemplatesModal.module.scss';

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

const emptyLine = () => ({
    description: '',
    debitAccount: '',
    creditAccount: '',
    amount: '',
    sortCodeId: '3',
    vat: 100,
    reference: '',
    reference2: '',
    journalEntryType: JournalEntryTypes.NormalJournal,
    displayOrder: 0,
});

const TAB_APPLY = 0;
const TAB_MANAGE = 1;

const JournalEntryTemplatesModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    direction = 'rtl',
    sortDate,
    debitAccountOptions = [],
    creditAccountOptions = [],
    sortCodeIdOptions = [],
    onApplied,
    initialTab = TAB_APPLY,
}) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(initialTab);

    // Shared templates list
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);

    // Manage tab
    const [saving, setSaving] = useState(false);
    const [view, setView] = useState('list');
    const [editingId, setEditingId] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [editLines, setEditLines] = useState([emptyLine()]);

    // Apply tab
    const [applying, setApplying] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [lineAmounts, setLineAmounts] = useState({});
    const [documentDate, setDocumentDate] = useState(new Date());
    const [reportingMonthDate, setReportingMonthDate] = useState(null);
    const [registrationMonthDate, setRegistrationMonthDate] = useState(null);

    const isRtl = direction === 'rtl';
    const textAlign = isRtl ? 'right' : 'left';

    const inputStyle = useMemo(() => ({
        style: { textAlign },
    }), [textAlign]);

    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);

    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const renderRtlAutocomplete = ({
        options,
        value,
        onChange,
        getOptionLabel,
        isOptionEqualToValue,
        placeholder = '',
    }) => (
        <Autocomplete
            className={fieldStyles.combo}
            size="small"
            fullWidth
            options={options}
            getOptionLabel={getOptionLabel}
            value={value}
            onChange={onChange}
            isOptionEqualToValue={isOptionEqualToValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    fullWidth
                    placeholder={placeholder}
                    className={fieldStyles.styleInput}
                    inputProps={{
                        ...params.inputProps,
                        ...inputStyle,
                    }}
                    InputProps={{
                        ...params.InputProps,
                        ...comboInputProps,
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                    <li
                        key={key}
                        {...optionProps}
                    >
                        {getOptionLabel(option)}
                    </li>
                );
            }}
            ListboxProps={{ direction }}
            PopperComponent={renderComboPopper}
        />
    );

    const debitOptions = useMemo(() => {
        const source = debitAccountOptions?.length ? debitAccountOptions : creditAccountOptions;
        return (source || []).map((o) => ({
            value: o.cardId,
            label: `${o.cardId} - ${o.name}`,
        }));
    }, [debitAccountOptions, creditAccountOptions]);

    const creditOptions = useMemo(() => {
        const source = creditAccountOptions?.length ? creditAccountOptions : debitAccountOptions;
        return (source || []).map((o) => ({
            value: o.cardId,
            label: `${o.cardId} - ${o.name}`,
        }));
    }, [creditAccountOptions, debitAccountOptions]);

    const vatOptions = useMemo(() => [
        { value: 100, label: '100%' },
        { value: Constants.Vat66, label: '66%' },
        { value: 25, label: '25%' },
        { value: 0, label: '0%' },
        { value: 16, label: t('AccountingTax') },
    ], [t]);

    const journalTypeOptions = useMemo(
        () => JOURNAL_ENTRY_TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: journalEntryTypeLabel(o.value, t),
        })),
        [t]
    );
    const accountLabel = useCallback((cardId) => {
        if (!cardId) return '—';
        const all = [...(debitAccountOptions || []), ...(creditAccountOptions || [])];
        const found = all.find((o) => o.cardId === cardId);
        return found ? `${found.cardId} - ${found.name}` : cardId;
    }, [debitAccountOptions, creditAccountOptions]);

    const fetchTemplates = useCallback(async () => {
        if (!caseId || !token) return;
        try {
            setLoading(true);
            const response = await JournalEntryTemplateService.GetByCaseId(caseId, token);
            if (response.ok) {
                const json = await response.json();
                setTemplates(json?.data || json || []);
            } else {
                toast.error(t('FailedToFetchTemplates') || 'Failed to fetch templates');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('FailedToFetchTemplates') || 'Failed to fetch templates');
        } finally {
            setLoading(false);
        }
    }, [caseId, token, t]);

    useEffect(() => {
        if (!open) return;
        setTab(initialTab);
        setView('list');
        setEditingId(null);
        setSelectedTemplate(null);
        setLineAmounts({});
        setDocumentDate(new Date());
        const month = sortDate ? new Date(sortDate) : new Date();
        setReportingMonthDate(month);
        setRegistrationMonthDate(month);
        fetchTemplates();
    }, [open, sortDate, fetchTemplates, initialTab]);

    const startCreate = () => {
        setEditingId(null);
        setName('');
        setDescription('');
        setEditLines([{ ...emptyLine(), displayOrder: 0 }]);
        setView('edit');
    };

    const startEdit = (template) => {
        setEditingId(template.id);
        setName(template.name || '');
        setDescription(template.description || '');
        const mapped = (template.lines || []).map((line, idx) => ({
            description: line.description || '',
            debitAccount: line.debitAccount || '',
            creditAccount: line.creditAccount || '',
            amount: Number(line.amount) > 0 ? line.amount : '',
            sortCodeId: line.sortCodeId || '3',
            vat: line.vat ?? 100,
            reference: line.reference || '',
            reference2: line.reference2 || '',
            journalEntryType: line.journalEntryType ?? JournalEntryTypes.NormalJournal,
            displayOrder: line.displayOrder ?? idx,
            id: line.id,
        }));
        setEditLines(mapped.length ? mapped : [{ ...emptyLine(), displayOrder: 0 }]);
        setView('edit');
    };

    const updateLine = (index, field, value) => {
        setEditLines((prev) => {
            const next = [...prev];
            const row = { ...next[index], [field]: value };

            if (field === 'sortCodeId') {
                const sortCode = sortCodeIdOptions.find((o) => o.sortCodeId === value);
                if (sortCode) {
                    row.vat = sortCode?.vat ?? row.vat;
                    if (sortCode.debitAccount) row.debitAccount = sortCode.debitAccount;
                    if (sortCode.creditAccount) row.creditAccount = sortCode.creditAccount;
                }
            }

            if (
                (field === 'debitAccount' || field === 'creditAccount') &&
                row.debitAccount &&
                row.creditAccount &&
                row.debitAccount === row.creditAccount
            ) {
                toast.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
                return prev;
            }

            next[index] = row;
            return next;
        });
    };

    const addLine = () => {
        setEditLines((prev) => [...prev, { ...emptyLine(), displayOrder: prev.length }]);
    };

    const removeLine = (index) => {
        setEditLines((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index).map((l, i) => ({ ...l, displayOrder: i }));
        });
    };

    const moveLine = (index, dir) => {
        setEditLines((prev) => {
            const target = index + dir;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            const tmp = next[index];
            next[index] = next[target];
            next[target] = tmp;
            return next.map((l, i) => ({ ...l, displayOrder: i }));
        });
    };

    const validateEdit = () => {
        if (!name.trim()) {
            toast.error(t('TemplateNameRequired') || 'Template name is required');
            return false;
        }
        if (!description.trim()) {
            toast.error(t('TemplateDescription') || 'Template description is required');
            return false;
        }
        if (!editLines.length) {
            toast.error(t('TemplateMustHaveLines') || 'Template must have at least one line');
            return false;
        }
        for (let i = 0; i < editLines.length; i++) {
            const raw = editLines[i].amount;
            if (raw === '' || raw == null) continue;
            const amount = Number(raw);
            if (Number.isNaN(amount) || amount < 0) {
                toast.error(t('TemplateLineAmountInvalid') || `Line ${i + 1}: amount must be a valid number`);
                return false;
            }
        }
        return true;
    };

    const parseOptionalAmount = (raw) => {
        if (raw === '' || raw == null) return 0;
        const amount = Number(raw);
        return Number.isNaN(amount) ? 0 : amount;
    };

    const handleSave = async () => {
        if (!validateEdit()) return;
        setSaving(true);
        try {
            const payloadLines = editLines.map((line, idx) => ({
                description: line.description || null,
                debitAccount: line.debitAccount || null,
                creditAccount: line.creditAccount || null,
                amount: parseOptionalAmount(line.amount),
                sortCodeId: line.sortCodeId || null,
                vat: line.vat == null ? null : Number(line.vat),
                reference: line.reference || null,
                reference2: line.reference2 || null,
                journalEntryType: line.journalEntryType ?? JournalEntryTypes.NormalJournal,
                displayOrder: idx,
            }));

            let response;
            if (editingId) {
                response = await JournalEntryTemplateService.Update({
                    id: editingId,
                    name: name.trim(),
                    description: description || null,
                    lines: payloadLines,
                }, token);
            } else {
                response = await JournalEntryTemplateService.Create({
                    caseId,
                    name: name.trim(),
                    description: description || null,
                    lines: payloadLines,
                }, token);
            }

            if (response.ok) {
                toast.success(t('TemplateSavedSuccessfully') || 'Template saved successfully');
                setView('list');
                await fetchTemplates();
            } else {
                const err = await response.json().catch(() => ({}));
                toast.error(err?.error || t('FailedToSaveTemplate') || 'Failed to save template');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('FailedToSaveTemplate') || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (template) => {
        const result = await Swal.fire({
            title: t('AreYouSure') || 'Are you sure?',
            text: t('DeleteTemplateConfirm') || `Delete template "${template.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Delete') || 'Delete',
            cancelButtonText: t('cancelButton') || 'Cancel',
        });
        if (!result.isConfirmed) return;

        try {
            const response = await JournalEntryTemplateService.Delete(template.id, token);
            if (response.ok) {
                toast.success(t('TemplateDeletedSuccessfully') || 'Template deleted');
                if (selectedTemplate?.id === template.id) {
                    setSelectedTemplate(null);
                    setLineAmounts({});
                }
                await fetchTemplates();
            } else {
                toast.error(t('FailedToDeleteTemplate') || 'Failed to delete template');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('FailedToDeleteTemplate') || 'Failed to delete template');
        }
    };

    const handleSelectTemplate = (template) => {
        setSelectedTemplate(template);
        const amounts = {};
        (template?.lines || []).forEach((line) => {
            const amount = Number(line.amount);
            amounts[line.id] = amount > 0 ? line.amount : '';
        });
        setLineAmounts(amounts);
    };

    const applyLines = useMemo(
        () => (selectedTemplate?.lines || []).slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
        [selectedTemplate]
    );

    const canApply = selectedTemplate && applyLines.length > 0 && documentDate && !applying;

    const handleApply = async () => {
        if (!selectedTemplate) return;

        for (const line of applyLines) {
            const amount = Number(lineAmounts[line.id]);
            if (!amount || amount <= 0 || Number.isNaN(amount)) {
                toast.error(t('TemplateLineAmountRequired') || 'Each line must have an amount greater than 0');
                return;
            }
        }

        setApplying(true);
        try {
            const toIso = (value) => {
                if (!value) return null;
                if (dayjs.isDayjs(value)) return value.toDate().toISOString();
                if (value instanceof Date) return value.toISOString();
                if (typeof value?.toISOString === 'function') return value.toISOString();
                return new Date(value).toISOString();
            };

            const dto = {
                templateId: selectedTemplate.id,
                caseId,
                accountantId,
                documentDate: toIso(documentDate),
                reportingMonthDate: toIso(reportingMonthDate),
                registrationMonthDate: toIso(registrationMonthDate),
                uploadDate: new Date().toISOString(),
                addAsProcessed: true,
                lineOverrides: applyLines.map((line) => ({
                    lineId: line.id,
                    amount: Number(lineAmounts[line.id]),
                })),
            };

            const response = await JournalEntryTemplateService.Apply(dto, token);
            if (response.ok) {
                const json = await response.json();
                const count = json?.journalEntryIds?.length || applyLines.length;
                toast.success(
                    (t('TemplateAppliedSuccessfully') || 'Inserted {{count}} journal entries').replace('{{count}}', count)
                );
                onClose?.();
                if (onApplied) await onApplied();
            } else {
                const err = await response.json().catch(() => ({}));
                toast.error(err?.error || t('FailedToApplyTemplate') || 'Failed to apply template');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('FailedToApplyTemplate') || 'Failed to apply template');
        } finally {
            setApplying(false);
        }
    };

    const renderManageList = () => (
        <>
            <div className={modalStyles.toolbar}>
                <p className={modalStyles.muted}>
                    {templates.length} {t('Templates') || 'templates'}
                </p>
                <button type="button" className={modalStyles.primaryBtn} onClick={startCreate}>
                    <AddIcon fontSize="small" />
                    {t('CreateTemplate') || 'Create template'}
                </button>
            </div>
            {loading ? (
                <div className={modalStyles.loading}><CircularProgress className={modalStyles.spinner} /></div>
            ) : templates.length === 0 ? (
                <div className={modalStyles.emptyState}>{t('NoTemplatesYet') || 'No templates yet'}</div>
            ) : (
                <div className={modalStyles.tableWrap}>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small" dir={direction}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align={textAlign}>{t('Name') || 'Name'}</TableCell>
                                    <TableCell align={textAlign}>{t('Description') || 'Description'}</TableCell>
                                    <TableCell align={textAlign}>{t('Lines') || 'Lines'}</TableCell>
                                    <TableCell align="center" className={modalStyles.actionsCell}>
                                        {t('Actions') || 'Actions'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templates.map((tpl) => (
                                    <TableRow key={tpl.id} hover>
                                        <TableCell align={textAlign}>{tpl.name}</TableCell>
                                        <TableCell align={textAlign}>{tpl.description || '—'}</TableCell>
                                        <TableCell align={textAlign}>{tpl.lines?.length || 0}</TableCell>
                                        <TableCell align="center" className={modalStyles.actionsCell}>
                                            <Tooltip title={t('Edit') || 'Edit'}>
                                                <IconButton size="small" onClick={() => startEdit(tpl)}><EditIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('Delete') || 'Delete'}>
                                                <IconButton size="small" color="error" onClick={() => handleDelete(tpl)}><DeleteIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}
        </>
    );

    const renderManageEdit = () => (
        <>
            <div className={modalStyles.formGrid}>
                <div className={modalStyles.formFields}>
                    <div className={modalStyles.formField}>
                        <span className={modalStyles.fieldLabel} dir={direction}>
                            {t('TemplateName') || t('Name') || 'Template name'}
                            <span className={modalStyles.requiredMark}> *</span>
                        </span>
                        <TextField
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            variant="standard"
                            placeholder={t('TemplateName') || t('Name') || 'Template name'}
                            className={`${fieldStyles.styleInput}${!name.trim() ? ` ${fieldStyles.styleInputRequired}` : ''}`}
                            InputProps={comboInputProps}
                            inputProps={{ style: { textAlign } }}
                        />
                    </div>
                    <div className={`${modalStyles.formField} ${modalStyles.formFieldWide}`}>
                        <span className={modalStyles.fieldLabel} dir={direction}>
                            {t('TemplateDescription') || t('Description') || 'Template description'}
                            <span className={modalStyles.requiredMark}> *</span>
                        </span>
                        <TextField
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            variant="standard"
                            placeholder={t('TemplateDescription') || t('Description') || 'Template description'}
                            className={`${fieldStyles.styleInput}${!description.trim() ? ` ${fieldStyles.styleInputRequired}` : ''}`}
                            InputProps={comboInputProps}
                            inputProps={{ style: { textAlign } }}
                        />
                    </div>
                </div>
                <IconButton
                    onClick={() => setView('list')}
                    className={modalStyles.backBtn}
                    aria-label={t('Back') || 'Back'}
                >
                    <ArrowBackIcon className={modalStyles.backIcon} />
                </IconButton>
            </div>

            <div className={modalStyles.toolbar}>
                <p className={modalStyles.sectionTitle}>{t('TemplateLines') || 'Template lines'}</p>
                <button type="button" className={modalStyles.ghostBtn} onClick={addLine}>
                    <AddIcon fontSize="small" />
                    {t('AddLine') || 'Add line'}
                </button>
            </div>

            <div className={modalStyles.tableWrap} style={{ maxHeight: 520 }}>
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        maxHeight: 520,
                        overflow: 'auto',
                        '& .MuiTableCell-root': {
                            verticalAlign: 'middle',
                            py: 1,
                            px: 1.25,
                            textAlign,
                        },
                    }}
                >
                <Table size="small" stickyHeader dir={direction} sx={{ minWidth: 1100, tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell align={textAlign} sx={{ width: 44 }}>#</TableCell>
                            <TableCell align={textAlign} sx={{ width: 130 }}>{t('SortCode') || 'Sort code'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 170 }}>{t('DebitAccount') || 'Debit'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 170 }}>{t('CreditAccount') || 'Credit'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 160 }}>{t('Description') || 'Description'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 110 }}>{t('Amount') || 'Amount'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 90 }}>{t('vat') || 'VAT'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 110 }}>{t('Reference') || 'Reference'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 110 }}>{t('Reference2') || 'Reference 2'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 140 }}>{t('JournalEntryType') || 'Type'}</TableCell>
                            <TableCell align={textAlign} sx={{ width: 108 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {editLines.map((line, index) => (
                            <TableRow key={index} hover>
                                <TableCell align={textAlign}>{index + 1}</TableCell>
                                <TableCell align={textAlign}>
                                    {renderRtlAutocomplete({
                                        options: sortCodeIdOptions,
                                        getOptionLabel: (o) => (o ? `${o.sortCodeId} - ${o.sortCodeName}` : ''),
                                        value: sortCodeIdOptions.find((o) => o.sortCodeId === line.sortCodeId) || null,
                                        onChange: (_, v) => updateLine(index, 'sortCodeId', v?.sortCodeId || ''),
                                        isOptionEqualToValue: (a, b) => a?.sortCodeId === b?.sortCodeId,
                                        placeholder: t('SortCode') || 'Sort code',
                                    })}
                                </TableCell>
                                <TableCell align={textAlign}>
                                    {renderRtlAutocomplete({
                                        options: debitOptions,
                                        getOptionLabel: (o) => o?.label || '',
                                        value: debitOptions.find((o) => o.value === line.debitAccount) || null,
                                        onChange: (_, v) => updateLine(index, 'debitAccount', v?.value || ''),
                                        isOptionEqualToValue: (a, b) => a?.value === b?.value,
                                        placeholder: t('DebitAccount') || 'Debit',
                                    })}
                                </TableCell>
                                <TableCell align={textAlign}>
                                    {renderRtlAutocomplete({
                                        options: creditOptions,
                                        getOptionLabel: (o) => o?.label || '',
                                        value: creditOptions.find((o) => o.value === line.creditAccount) || null,
                                        onChange: (_, v) => updateLine(index, 'creditAccount', v?.value || ''),
                                        isOptionEqualToValue: (a, b) => a?.value === b?.value,
                                        placeholder: t('CreditAccount') || 'Credit',
                                    })}
                                </TableCell>
                                <TableCell align={textAlign}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        fullWidth
                                        className={fieldStyles.styleInput}
                                        value={line.description}
                                        onChange={(e) => updateLine(index, 'description', e.target.value)}
                                        InputProps={comboInputProps}
                                        inputProps={{ style: { textAlign } }}
                                    />
                                </TableCell>
                                <TableCell align={textAlign}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        fullWidth
                                        type="number"
                                        className={fieldStyles.styleInput}
                                        value={line.amount}
                                        onChange={(e) => updateLine(index, 'amount', e.target.value)}
                                        InputProps={comboInputProps}
                                        placeholder={t('Optional')} inputProps={{ min: 0, step: '0.01', style: { textAlign } }}
                                    />
                                </TableCell>
                                <TableCell align={textAlign}>
                                    <AutocompleteSelectFilter
                                        options={vatOptions}
                                        selectedValues={
                                            vatOptions.find((o) => o.value === (line.vat ?? 100))
                                            || { value: line.vat ?? 100, label: `${line.vat ?? 100}%` }
                                        }
                                        onChange={(_, option) => updateLine(index, 'vat', option?.value ?? 100)}
                                        placeholder={t('vat') || 'VAT'}
                                        isMultiple={false}
                                        direction={direction}
                                        portalMenu
                                    />
                                </TableCell>
                                <TableCell align={textAlign}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        fullWidth
                                        className={fieldStyles.styleInput}
                                        value={line.reference}
                                        onChange={(e) => updateLine(index, 'reference', e.target.value)}
                                        InputProps={comboInputProps}
                                        inputProps={{ style: { textAlign } }}
                                    />
                                </TableCell>
                                <TableCell align={textAlign}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        fullWidth
                                        className={fieldStyles.styleInput}
                                        value={line.reference2}
                                        onChange={(e) => updateLine(index, 'reference2', e.target.value)}
                                        InputProps={comboInputProps}
                                        inputProps={{ style: { textAlign } }}
                                    />
                                </TableCell>
                                <TableCell align={textAlign}>
                                    <AutocompleteSelectFilter
                                        options={journalTypeOptions}
                                        selectedValues={
                                            journalTypeOptions.find(
                                                (o) => o.value === (line.journalEntryType ?? JournalEntryTypes.NormalJournal)
                                            )
                                            || {
                                                value: line.journalEntryType ?? JournalEntryTypes.NormalJournal,
                                                label: journalEntryTypeLabel(
                                                    line.journalEntryType ?? JournalEntryTypes.NormalJournal,
                                                    t
                                                ),
                                            }
                                        }
                                        onChange={(_, option) => updateLine(
                                            index,
                                            'journalEntryType',
                                            option?.value ?? JournalEntryTypes.NormalJournal
                                        )}
                                        placeholder={t('JournalEntryType') || 'Type'}
                                        isMultiple={false}
                                        direction={direction}
                                        portalMenu
                                    />
                                </TableCell>
                                <TableCell align={textAlign} sx={{ whiteSpace: 'nowrap' }}>
                                    <IconButton size="small" disabled={index === 0} onClick={() => moveLine(index, -1)}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                                    <IconButton size="small" disabled={index === editLines.length - 1} onClick={() => moveLine(index, 1)}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                                    <IconButton size="small" color="error" disabled={editLines.length <= 1} onClick={() => removeLine(index)}><DeleteIcon fontSize="inherit" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            </div>
        </>
    );

    const renderApplyTab = () => (
        <>
            {loading ? (
                <div className={modalStyles.loading}><CircularProgress className={modalStyles.spinner} /></div>
            ) : (
                <>
                    <div className={modalStyles.selectTemplate}>
                        <span className={modalStyles.selectTemplateLabel} dir={direction}>
                            {t('SelectTemplate') || 'Select template'}
                        </span>
                        <div className={modalStyles.selectTemplateField}>
                            <Autocomplete
                                className={fieldStyles.combo}
                                size="small"
                                fullWidth
                                options={templates}
                                getOptionLabel={(o) => o?.name || ''}
                                value={selectedTemplate}
                                onChange={(_, v) => handleSelectTemplate(v)}
                                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                noOptionsText={t('NoTemplatesYet') || 'No templates yet'}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('SelectTemplate') || 'Select template'}
                                        variant="standard"
                                        size="small"
                                        fullWidth
                                        className={fieldStyles.styleInput}
                                        InputProps={{
                                            ...params.InputProps,
                                            ...comboInputProps,
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            ...inputStyle,
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li
                                            key={option.id ?? key}
                                            {...optionProps}
                                        >
                                            <Box sx={{ width: '100%' }}>
                                                <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                                                {option.description ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.description}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </li>
                                    );
                                }}
                                ListboxProps={{ direction }}
                                PopperComponent={renderComboPopper}
                            />
                        </div>
                        {selectedTemplate?.description && (
                            <p className={`${modalStyles.muted} ${modalStyles.selectTemplateDesc}`}>
                                {selectedTemplate.description}
                            </p>
                        )}
                    </div>

                    {selectedTemplate && (
                        <>
                            <div className={modalStyles.dateRow} dir={direction}>
                                {[
                                    {
                                        key: 'documentDate',
                                        label: t('DocumentDate') || 'Document date',
                                        node: (
                                            <CustomDatePicker
                                                date={documentDate ? new Date(documentDate) : null}
                                                onDateChange={(date) => {
                                                    if (date) setDocumentDate(date);
                                                }}
                                                height="40px"
                                                label=""
                                            />
                                        ),
                                    },
                                    {
                                        key: 'reportingMonth',
                                        label: t('MonthReportDate') || 'Reporting month',
                                        node: (
                                            <CustomMonthDatePicker
                                                date={reportingMonthDate ? dayjs(reportingMonthDate) : null}
                                                onDateChange={(date) => {
                                                    if (date) setReportingMonthDate(date);
                                                }}
                                                height="40px"
                                                width="100%"
                                            />
                                        ),
                                    },
                                    {
                                        key: 'registrationMonth',
                                        label: t('RegistrationMonthDate') || 'Registration month',
                                        node: (
                                            <CustomMonthDatePicker
                                                date={registrationMonthDate ? dayjs(registrationMonthDate) : null}
                                                onDateChange={(date) => {
                                                    if (date) setRegistrationMonthDate(date);
                                                }}
                                                height="40px"
                                                width="100%"
                                            />
                                        ),
                                    },
                                ].map((field) => (
                                    <div key={field.key} className={modalStyles.dateField} dir={direction}>
                                        <span className={modalStyles.fieldLabel}>{field.label}</span>
                                        {field.node}
                                    </div>
                                ))}
                            </div>

                            <div className={modalStyles.tableWrap}>
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiTableCell-root': {
                                            py: 1.25,
                                            px: 1.5,
                                            textAlign,
                                        },
                                    }}
                                >
                                    <Table size="small" dir={direction} sx={{ minWidth: 900 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align={textAlign} sx={{ width: 48 }}>#</TableCell>
                                                <TableCell align={textAlign}>{t('Description') || 'Description'}</TableCell>
                                                <TableCell align={textAlign}>{t('DebitAccount') || 'Debit'}</TableCell>
                                                <TableCell align={textAlign}>{t('CreditAccount') || 'Credit'}</TableCell>
                                                <TableCell align={textAlign}>{t('Reference') || 'Reference'}</TableCell>
                                                <TableCell align={textAlign}>{t('JournalEntryType') || 'Type'}</TableCell>
                                                <TableCell align={textAlign} sx={{ width: 140 }}>{t('Amount') || 'Amount'}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {applyLines.map((line, idx) => (
                                                <TableRow key={line.id} hover>
                                                    <TableCell align={textAlign}>{idx + 1}</TableCell>
                                                    <TableCell align={textAlign}>{line.description || '—'}</TableCell>
                                                    <TableCell align={textAlign}>{accountLabel(line.debitAccount)}</TableCell>
                                                    <TableCell align={textAlign}>{accountLabel(line.creditAccount)}</TableCell>
                                                    <TableCell align={textAlign}>{line.reference || '—'}</TableCell>
                                                    <TableCell align={textAlign}>{journalEntryTypeLabel(line.journalEntryType, t)}</TableCell>
                                                    <TableCell align={textAlign}>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            fullWidth
                                                            variant="standard"
                                                            className={fieldStyles.styleInput}
                                                            value={lineAmounts[line.id] ?? ''}
                                                            onChange={(e) =>
                                                                setLineAmounts((prev) => ({
                                                                    ...prev,
                                                                    [line.id]: e.target.value,
                                                                }))
                                                            }
                                                            InputProps={comboInputProps}
                                                            inputProps={{ min: 0, step: '0.01', style: { textAlign } }}
                                                            placeholder={Number(line.amount) > 0 ? String(beautifyNumber(line.amount)) : ''}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </>
                    )}

                    {!loading && templates.length === 0 && (
                        <div className={modalStyles.emptyState}>
                            {t('NoTemplatesYet') || 'No templates yet. Create one first.'}
                        </div>
                    )}
                </>
            )}
        </>
    );

    const showTabs = !(tab === TAB_MANAGE && view === 'edit');
    const isExpandedModal =
        (tab === TAB_MANAGE && view === 'edit')
        || (tab === TAB_APPLY && !!selectedTemplate);

    if (!open) return null;

    const headerTitle = !showTabs
        ? (editingId ? (t('EditTemplate') || 'Edit template') : (t('CreateTemplate') || 'Create template'))
        : (t('JournalEntryTemplates') || 'Journal Entry Templates');

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${direction === 'rtl' ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div
                        className={`${modalStyles.modal}${isExpandedModal ? ` ${modalStyles.modalEdit}` : ''}`}
                    >
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={modalStyles.closeIcon}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{headerTitle}</div>
                                <div />
                            </div>

                            {showTabs && (
                                <div className={modalStyles.tabs}>
                                    <button
                                        type="button"
                                        className={`${modalStyles.tab}${tab === TAB_APPLY ? ` ${modalStyles.tabActive}` : ''}`}
                                        onClick={() => setTab(TAB_APPLY)}
                                    >
                                        {t('InsertFromTemplate') || 'Insert from template'}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${modalStyles.tab}${tab === TAB_MANAGE ? ` ${modalStyles.tabActive}` : ''}`}
                                        onClick={() => {
                                            setTab(TAB_MANAGE);
                                            setView('list');
                                        }}
                                    >
                                        {t('ManageTemplates') || t('Templates') || 'Manage templates'}
                                    </button>
                                </div>
                            )}

                            <div className={modalStyles.scroll}>
                                {tab === TAB_APPLY && renderApplyTab()}
                                {tab === TAB_MANAGE && (view === 'list' ? renderManageList() : renderManageEdit())}
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose}>
                                    {t('cancelButton') || 'Cancel'}
                                </button>
                                {tab === TAB_MANAGE && view === 'edit' && (
                                    <button
                                        type="button"
                                        className={modalStyles.primaryBtn}
                                        disabled={saving}
                                        onClick={handleSave}
                                    >
                                        {saving ? <CircularProgress className={modalStyles.spinner} size={18} color="inherit" /> : (t('save') || 'Save')}
                                    </button>
                                )}
                                {tab === TAB_APPLY && (
                                    <button
                                        type="button"
                                        className={modalStyles.primaryBtn}
                                        disabled={!canApply}
                                        onClick={handleApply}
                                    >
                                        {applying
                                            ? <CircularProgress className={modalStyles.spinner} size={18} color="inherit" />
                                            : (t('InsertJournalEntries') || 'Insert journal entries')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default JournalEntryTemplatesModal;
